import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchableSelect from '../SearchableSelect'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { searchCustomers } from '../../lib/customerSearch'
import { btnPrimary, btnSecondary, inputCls } from '../../pages/Warehouse/types'

// A group job's reference is typed by staff (e.g. TEMU202611): it never comes from the KI/KE sequence. Up to 25 characters, because every
// shipment row under it is "<ref>-NN" and a job reference may be at most 30. The customer is who the month's invoice goes to.
const GROUP_REF = /^[A-Z0-9-]{1,25}$/

export default function NewGroupDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const { userEmail } = useAuth()
  const [ref, setRef] = useState('')
  const [customer, setCustomer] = useState<{ id: string; label: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const refOk = GROUP_REF.test(ref)
  // SearchableSelect reports only the picked id, so remember the labels of what the search returned.
  const [seen] = useState(() => new Map<string, string>())
  const search = async (term: string) => {
    const opts = (await searchCustomers({ term })).map(c => ({ id: c.id, code: c.code, name: c.name, label: c.name }))
    opts.forEach(o => seen.set(o.id, o.label))
    return opts
  }

  async function create() {
    if (!refOk) { setError('Reference: 1 to 25 characters, capital letters, digits and hyphens only (e.g. TEMU202611).'); return }
    if (!customer) { setError('Choose the customer the monthly invoice goes to.'); return }
    setBusy(true); setError(null)
    const { data, error: e } = await supabase.from('jobs').insert({
      job_ref: ref, direction: 'IMP', transport_mode: 'AIR', job_type: 'GROUP', status: 'PRE',
      invoice_to_id: customer.id, created_by: userEmail, import_source: 'GROUP',
    }).select('id').single()
    setBusy(false)
    if (e) {
      setError(e.code === '23505' ? `A job with reference ${ref} already exists.` : e.message)
      return
    }
    navigate(`/jobs/${(data as { id: string }).id}`)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">New Group</h2>
          <button onClick={onClose} disabled={busy} className="text-gray-500 hover:text-gray-700 disabled:opacity-50">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">A group gathers a customer's shipments (for example one month of air freight) under one reference. Its shipments are numbered {ref || 'TEMU202611'}-01, -02 …</p>
          <label className="block">
            <span className="text-xs text-gray-600 dark:text-gray-400">Group reference</span>
            <input className={`${inputCls} mt-1 font-mono font-bold`} value={ref} placeholder="TEMU202611" onChange={e => setRef(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} maxLength={25} />
          </label>
          <div>
            <span className="text-xs text-gray-600 dark:text-gray-400">Customer (invoice to)</span>
            <div className="mt-1">
              <SearchableSelect options={[]} value={customer?.id || ''} allowCreate={false} usePortal selectedOption={customer} placeholder="Search customer…"
                onChange={id => setCustomer(id ? { id, label: seen.get(id) ?? '…' } : null)} onSearch={search} />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
          <button onClick={onClose} disabled={busy} className={btnSecondary}>Cancel</button>
          <button onClick={create} disabled={busy || !refOk || !customer} className={btnPrimary}>{busy ? 'Creating…' : 'Create group'}</button>
        </div>
      </div>
    </div>
  )
}
